import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef } from "react";

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size, className }), className)}
        {...props}>
        {children}
      </button>
    );
  }
);

export default Button;

const baseClasses =
  "rounded-2xl font-medium font-poppins enabled:transition enabled:duration-300 enabled:ease-in-out disabled:cursor-not-allowed focus:outline-none";

const primaryClasses = {
  main: "bg-nt-secondary text-white",
  hover: "enabled:hover:bg-nt-secondary-600",
  active:
    "enabled:active:bg-nt-secondary enabled:active:border enabled:active:border-nt-secondary-700 enabled:active:box-border",
  disabled: "disabled:bg-nt-neutral-300 disabled:text-nt-neutral-800",
};

const secondaryClasses = {
  main: "bg-transparent text-nt-secondary border border-nt-secondary box-border",
  hover: "enabled:hover:bg-nt-secondary-100",
  active: "enabled:active:border-nt-secondary-700",
  disabled: "disabled:border-nt-neutral-400 disabled:text-nt-neutral-400",
};

const tertiaryClasses = {
  main: "bg-transparent text-nt-neutral-800 border border-nt-neutral-800 box-border",
  hover: "enabled:hover:bg-nt-neutral-100",
  active: "enabled:active:border-nt-neutral-950",
  disabled: "disabled:border-nt-neutral-400 disabled:text-nt-neutral-400",
};

const ghostClasses = {
  main: "bg-transparent text-nt-primary-900",
  hover: "enabled:hover:text-nt-primary-950",
  active: "enabled:active:text-nt-primary-950",
  disabled: "disabled:text-nt-neutral-600",
};

const buttonVariants = cva(baseClasses, {
  variants: {
    variant: {
      primary: cn(
        primaryClasses.main,
        primaryClasses.hover,
        primaryClasses.active,
        primaryClasses.disabled
      ),
      secondary: cn(
        secondaryClasses.main,
        secondaryClasses.hover,
        secondaryClasses.active,
        secondaryClasses.disabled
      ),
      tertiary: cn(
        tertiaryClasses.main,
        tertiaryClasses.hover,
        tertiaryClasses.active,
        tertiaryClasses.disabled
      ),
      ghost: cn(
        ghostClasses.main,
        ghostClasses.hover,
        ghostClasses.active,
        ghostClasses.disabled
      ),
    },
    size: {
      icon: "p-0",
      sm: "px-3 py-2 text-sm",
      base: "px-4 py-3 text-base",
      lg: "px-6 py-4 text-lg",
    },
  },
  defaultVariants: {
    variant: "primary",
    size: "base",
  },
});

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
